import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Users, Camera, User, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Race, Participant } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { SwissIdDisplay } from '@/components/ui/copy-button';
import { ApplePhotoGallery } from '@/components/ApplePhotoGallery';
import { GroupedRaceView } from '@/components/GroupedRaceView';
import { JobLogsModal } from '@/components/JobLogsModal';
import { useTranslation } from 'react-i18next';
import { getParticipantRanges, getCachedParticipantPhotos } from '@/lib/participantRangesCache';

interface RaceDetailProps {
  race: Race;
  eventName: string;
  eventSwissId: string;
  onBack: () => void;
  onParticipantSelect: (participant: Participant) => void;
  onCartClick?: () => void;
}

export const RaceDetail = ({ race, eventName, eventSwissId, onBack, onParticipantSelect, onCartClick }: RaceDetailProps) => {
  const { t } = useTranslation();
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [showGallery, setShowGallery] = useState(false);
  const [participantThumbnails, setParticipantThumbnails] = useState<Record<string, string[]>>({});
  const [hoveredParticipant, setHoveredParticipant] = useState<string | null>(null);
  const [lazyLoadedImages, setLazyLoadedImages] = useState<Record<string, boolean>>({});
  const [scrollPositions, setScrollPositions] = useState<Record<string, number>>({});
  const [showJobLogs, setShowJobLogs] = useState(false);

  useEffect(() => {
    console.log('RaceDetail race data:', race);
    console.log('RaceDetail batch_job_id:', race.batch_job_id);
  }, [race]);

  useEffect(() => {
    fetchParticipants();
  }, [race.id]);

  const fetchParticipants = async () => {
    try {
      const { data, error } = await supabase
        .from('participants')
        .select('id, race_id, swiss_equestrian_rider_id, swiss_equestrian_horse_id, rider_name, horse_name, starting_number, race_order, photo_to, total_photos, group_id, participant_sequence_id, created_at, updated_at')
        .eq('race_id', race.id)
        .order('race_order', { ascending: true, nullsFirst: false })
        .order('starting_number', { ascending: true, nullsFirst: false });

      if (error) {
        console.error('Error fetching participants:', error);
        return;
      }

      setParticipants(data || []);
      
      // Load thumbnails for each participant
      if (data) {
        loadParticipantThumbnails(data);
      }
    } catch (error) {
      console.error('Error fetching participants:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadParticipantThumbnails = async (participants: Participant[]) => {
    try {
      const result = await getParticipantRanges(race.id, eventSwissId);
      setParticipantThumbnails(result.thumbnails);
      if (Array.isArray(result.participantRanges)) {
        setParticipants(prev => prev.map(p => {
          const pr = result.participantRanges.find((r: any) => r.id === p.id);
          const newTotal = pr?.photo_range?.file_ids?.length ?? p.total_photos;
          return pr ? { ...p, total_photos: newTotal } : p;
        }));
      }
    } catch (e) {
      console.error('Failed loading participant thumbnails:', e);
      setParticipantThumbnails({});
    }
  };
  const handleMouseEnter = async (participant: Participant) => {
    setHoveredParticipant(participant.id);
    // Preload first 20 images from the assigned range
    if (!lazyLoadedImages[participant.id] && participantThumbnails[participant.id]) {
      setLazyLoadedImages(prev => ({ ...prev, [participant.id]: true }));
      try {
        participantThumbnails[participant.id].slice(0, 20).forEach((url) => {
          const img = new Image();
          img.src = url;
        });
      } catch (error) {
        console.error(`Error preloading images for participant ${participant.id}:`, error);
      }
    }
  };
  const handleMouseLeave = () => {
    setHoveredParticipant(null);
  };

  const handleParticipantClick = (participant: Participant) => {
    if (race.status === 'uploaded' && participant.total_photos && participant.total_photos > 0) {
      setSelectedParticipant(participant);
      setShowGallery(true);
    } else {
      onParticipantSelect(participant);
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'B100': return 'bg-red-100 text-red-800 border-red-200';
      case 'B/R 90': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'L80': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Button onClick={onBack} variant="ghost" className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('raceDetail.backToEvent')} {eventName}
        </Button>
        
        <div className="animate-pulse space-y-6">
          <div className="h-32 bg-muted rounded-lg"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-48 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Check if this race is in GROUPED mode
  if (race.race_type === 'GROUPED') {
    return (
      <GroupedRaceView
        race={race}
        eventName={eventName}
        eventSwissId={eventSwissId}
        onBack={onBack}
        onCartClick={onCartClick}
      />
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Back Button */}
      <Button 
        onClick={onBack} 
        variant="ghost" 
        className="mb-6 hover:bg-primary/10 text-primary"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {t('raceDetail.backToEvent')} {eventName}
      </Button>

      {/* Race Header */}
      <Card className="mb-8 shadow-card">
        <CardHeader>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Badge className={getCategoryColor(race.category)}>
                {race.category}
              </Badge>
            </div>
            <Badge variant="outline">
              {participants.length} {t('raceDetail.participants')}
            </Badge>
          </div>
          <CardTitle className="text-2xl md:text-3xl font-bold text-primary">
            {race.name}
          </CardTitle>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mt-2">
            <span>{t('raceDetail.date')}: {new Date(race.race_date).toLocaleDateString()}</span>
            {race.race_time && <span>{t('raceDetail.time')}: {race.race_time}</span>}
          </div>
        </CardHeader>
      </Card>

      {/* Participants Grid */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold mb-4 flex items-center">
          <Users className="w-6 h-6 mr-2 text-primary" />
          {t('raceDetail.participantsAndPhotos')}
        </h2>
        <p className="text-muted-foreground mb-4">
          {t('raceDetail.description')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {participants.map((participant) => {
          const isHovered = hoveredParticipant === participant.id;
          const hasPhotos = participantThumbnails[participant.id]?.length > 0;
          
          return (
            <Card 
              key={participant.id}
              className="group hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-[1.02] overflow-hidden"
              onClick={() => handleParticipantClick(participant)}
              onMouseEnter={() => handleMouseEnter(participant)}
              onMouseLeave={handleMouseLeave}
            >
              <div className="relative h-32 bg-gradient-to-br from-primary/10 to-primary/5 group-hover:from-primary/20 group-hover:to-primary/10 transition-all duration-300">
                {/* Real thumbnail preview with scrolling animation */}
                <div className="absolute inset-2 overflow-hidden rounded-sm">
                  <div 
                    className={`flex gap-1 h-full ${isHovered && hasPhotos ? 'animate-scroll-smooth' : ''} transition-transform ease-linear`}
                    style={{
                      width: hasPhotos && participantThumbnails[participant.id]?.length > 6 ? `${(participantThumbnails[participant.id].length * 100) / 6}%` : '100%',
                      animationDuration: hasPhotos ? `${(participantThumbnails[participant.id]?.length || 0) * 2}s` : '0s',
                      animationIterationCount: 'infinite',
                      animationDirection: 'normal',
                      animationFillMode: 'forwards',
                      animationPlayState: isHovered ? 'running' : 'paused'
                    }}
                  >
                    {/* Display all available images */}
                    {participantThumbnails[participant.id]?.map((thumbnailUrl, i) => (
                      <div 
                        key={`img-${i}`}
                        className="flex-shrink-0 bg-primary/20 rounded-sm overflow-hidden aspect-square"
                        style={{ 
                          width: `${100/6}%`
                        }}
                      >
                        <img 
                          src={thumbnailUrl} 
                          alt={`Preview ${i + 1}`}
                          className="w-full h-full object-cover transition-opacity duration-300"
                          loading="lazy"
                        />
                      </div>
                    )) || [...Array(6)].map((_, i) => (
                      <div 
                        key={`placeholder-${i}`}
                        className="flex-shrink-0 bg-primary/20 rounded-sm animate-pulse"
                        style={{ 
                          width: `${100/6}%`,
                          animationDelay: `${i * 0.2}s`,
                          animationDuration: '2s'
                        }}
                      />
                    ))}
                  </div>
                </div>
                
                <div className="absolute top-2 right-2">
                  <Badge className="bg-black/80 text-white border-0 text-xs shadow-lg">
                    {(participants.find(p => p.id === participant.id)?.total_photos ?? 0)} {t('participants.photos')}
                  </Badge>
                </div>
                
                {participant.starting_number && (
                  <div className="absolute top-2 left-2">
                    <Badge variant="outline" className="bg-white/95 text-xs shadow-lg">
                      #{participant.starting_number}
                    </Badge>
                  </div>
                )}
                
                {/* Enhanced text overlay with better contrast */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-3">
                  <div className="text-white text-sm font-semibold truncate drop-shadow-lg">
                    {participant.rider_name}
                  </div>
                  <div className="text-white/90 text-xs truncate drop-shadow-md">
                    {participant.horse_name}
                  </div>
                </div>
              </div>
              
              <CardContent className="p-3">
                <Button 
                  size="sm"
                  className="w-full bg-primary hover:bg-primary/90"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleParticipantClick(participant);
                  }}
                >
                  <Camera className="w-4 h-4 mr-2" />
                  {t('participants.viewGallery')}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {participants.length === 0 && !loading && (
        <div className="text-center py-12">
          <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">{t('raceDetail.noParticipants')}</h3>
          <p className="text-muted-foreground">
            {t('raceDetail.noParticipantsDescription')}
          </p>
        </div>
      )}

      {/* Photo Gallery Modal */}
      {showGallery && selectedParticipant && (() => {
        const cached = getCachedParticipantPhotos(race.id, eventSwissId, selectedParticipant.id);
        return (
          <ApplePhotoGallery
            participant={selectedParticipant}
            eventId={eventSwissId}
            raceId={race.id}
            swissRaceId={race.swiss_equestrian_race_id}
            eventName={eventName}
            raceCategory={race.category}
            onClose={() => {
              setShowGallery(false);
              setSelectedParticipant(null);
            }}
            onCartClick={onCartClick}
            initialPhotoUrls={cached?.urls}
            initialPhotoIds={cached?.fileIds}
          />
        );
      })()}

      {/* Job Logs Modal */}
      {race.batch_job_id && (
        <JobLogsModal
          jobId={race.batch_job_id}
          isOpen={showJobLogs}
          onClose={() => setShowJobLogs(false)}
        />
      )}
    </div>
  );
};